import { Routes, Route } from '@angular/router';
import { Type } from '@angular/core';
import { EntityRouteGuard } from '@overview/services/entity-route-guard';
import { ListerRouteContext, RouteContextItemType } from '@context/old_index';
import { OidcAuthRouteGuardService } from '@auth/services/oidc-auth-route-guard';
import { TenantParentListPageComponent } from
  '@feature/_app_modules/mojo-dashboard-mci-common/tenant-parent-lister/container/tenant-parent-list-page.component';
import { ManagerListPageComponent } from '@mojo-mci/manager-list-page/manager-list-page.component';
import { ClientParentListPageComponent } from '@mojo-mci/client-parent-list-page/client-parent-list-page.component';
import { ClientListPageComponent } from '@mojo-mci/client-list-page/client-list-page.component';
import { EstateListPageComponent } from '@mojo-mci/estate-list-page/estate-list-page.component';
import { PropertyListPageComponent } from '@mojo-mci/property-list-page/property-list-page.component';
import { HeadleaseListPageComponent } from '@mojo-mci/headlease-list-page/headlease-list-page.component';
import { LandlordListPageComponent } from '@mojo-mci/landlord-list-page/landlord-list-page.component';
import { UnitListPageComponent } from '@mojo-mci/unit-list-page/unit-list-page.component';
import { LeaseListPageComponent } from '@mojo-mci/lease-list-page/lease-list-page.component';
import { TenantListPageComponent } from '@mojo-mci/tenant-list-page/tenant-list-page.component';
import { DebtorListPageComponent } from '@mojo-mci/debtor-list-page/debtor-list-page.component';
import { CreditorListPageComponent } from '@mojo-mci/creditor-list-page/creditor-list-page.component';
import { VacantListPageComponent } from '@mojo-mci/vacant-list-page/vacant-list-page.component';
import { SupplierListPageComponent } from '@feature/_app_modules/mojo-dashboard-mci-common/supplier-list-page/supplier-list-page.component';

interface ListerItemData {
  itemType: RouteContextItemType,
  idParamName: string,
  dashboardPathComponent: string,
  listerPathComponent: string,
  component: Type<any>,
}


/**
 * produces a route for a lister page of allItemData for the forItemData
 * e.g listerRoute( estate, client ) would produce a route for the estate lister page for a specified client
 */
export function listerRoute(allItemData: ListerItemData, forItemData: ListerItemData, pathOverride?: string ): Route {
  return {
    data: {
      routeContext: ListerRouteContext.allFor(allItemData.itemType, forItemData.itemType),
    },
    path: pathOverride ? pathOverride : `${forItemData.dashboardPathComponent}/:${forItemData.idParamName}/${allItemData.listerPathComponent}`,
    component: allItemData.component,
    canActivate: [OidcAuthRouteGuardService, EntityRouteGuard]
  };
}

export function unitRoute(allItemData: ListerItemData): Route {
  return listerRoute(allItemData,
    unit,
    `property/:propertyId/${unit.dashboardPathComponent}/:${unit.idParamName}/${allItemData.listerPathComponent}`);
}

export function vacantRoute(allItemData: ListerItemData): Route {
  return listerRoute(allItemData,
    vacant,
    `property/:propertyId/${vacant.dashboardPathComponent}/:${vacant.idParamName}/${allItemData.listerPathComponent}`);
}

export function headleaseRoute(allItemData: ListerItemData): Route {
  return listerRoute(allItemData,
    unit,
    `property/:propertyId/${headlease.dashboardPathComponent}/:${headlease.idParamName}/${allItemData.listerPathComponent}`);
}

export function allRoute(allItemData: ListerItemData): Route {
  return {
    data: {
      routeContext: ListerRouteContext.all(allItemData.itemType),
    },
    path: `${allItemData.listerPathComponent}`,
    component: allItemData.component,
    canActivate: [OidcAuthRouteGuardService, EntityRouteGuard]
  };
}




export const client: ListerItemData = {
  itemType: 'Client',
  idParamName: 'clientId',
  dashboardPathComponent: 'client',
  listerPathComponent: 'clients',
  component: ClientListPageComponent
};

export const clientParent: ListerItemData = {
  itemType: 'ClientParent',
  idParamName: 'clientParentId',
  dashboardPathComponent: 'client-parent',
  listerPathComponent: 'client-parents',
  component: ClientParentListPageComponent
};

export const creditor: ListerItemData = {
  itemType: 'Creditor',
  idParamName: 'creditorId',
  dashboardPathComponent: 'creditor',
  listerPathComponent: 'creditors',
  component: CreditorListPageComponent
};

export const debtor: ListerItemData = {
  itemType: 'Debtor',
  idParamName: 'debtorId',
  dashboardPathComponent: 'debtor',
  listerPathComponent: 'debtors',
  component: DebtorListPageComponent
};

export const estate: ListerItemData = {
  itemType: 'Estate',
  idParamName: 'estateId',
  dashboardPathComponent: 'estate',
  listerPathComponent: 'estates',
  component: EstateListPageComponent
};

export const headlease: ListerItemData = {
  itemType: 'Headlease',
  idParamName: 'headleaseId',
  dashboardPathComponent: 'headlease',
  listerPathComponent: 'headleases',
  component: HeadleaseListPageComponent
};

export const landlord: ListerItemData = {
  itemType: 'Landlord',
  idParamName: 'landlordId',
  dashboardPathComponent: 'landlord',
  listerPathComponent: 'landlords',
  component: LandlordListPageComponent
};

export const lease: ListerItemData = {
  itemType: 'Lease',
  idParamName: 'leaseId',
  dashboardPathComponent: 'lease',
  listerPathComponent: 'leases',
  component: LeaseListPageComponent
};

export const manager: ListerItemData = {
    itemType: 'Manager',
    idParamName: 'managerId',
    dashboardPathComponent: 'manager',
    listerPathComponent: 'managers',
    component: ManagerListPageComponent
  };

export const property: ListerItemData = {
  itemType: 'Property',
  idParamName: 'propertyId',
  dashboardPathComponent: 'property',
  listerPathComponent: 'properties',
  component: PropertyListPageComponent
};

export const supplier: ListerItemData = {
  itemType: 'Supplier',
  idParamName: 'supplierId',
  dashboardPathComponent: 'supplier',
  listerPathComponent: 'suppliers',
  component: SupplierListPageComponent
};

export const tenant: ListerItemData = {
  itemType: 'Tenant',
  idParamName: 'tenantId',
  dashboardPathComponent: 'tenant',
  listerPathComponent: 'tenants',
  component: TenantListPageComponent
};

export const tenantParent: ListerItemData = {
  itemType: 'TenantParent',
  idParamName: 'tenantParentId',
  dashboardPathComponent: 'tenant-parent',
  listerPathComponent: 'tenant-parents',
  component: TenantParentListPageComponent
};

export const unit: ListerItemData = {
  itemType: 'Unit',
  idParamName: 'unitId',
  dashboardPathComponent: 'unit',
  listerPathComponent: 'units',
  component: UnitListPageComponent
};

export const vacant: ListerItemData = {
  itemType: 'Vacant',
  idParamName: 'vacantId',
  dashboardPathComponent: 'vacant',
  listerPathComponent: 'vacants',
  component: VacantListPageComponent
};



// due to angular AOT constraints we can't generate these combinations via code
export const listerRoutes: Routes = [

  // Estate
  allRoute(estate),
  listerRoute(estate, client),
  listerRoute(estate, clientParent),
  listerRoute(estate, creditor),
  listerRoute(estate, debtor),
  headleaseRoute(estate),
  listerRoute(estate, lease),
  listerRoute(estate, landlord),
  listerRoute(estate, manager),
  listerRoute(estate, property),
  listerRoute(estate, supplier),
  listerRoute(estate, tenant),
  listerRoute(estate, tenantParent),
  unitRoute(estate),
  vacantRoute(estate),

  // Client
  allRoute(client),
  listerRoute(client, clientParent),
  listerRoute(client, creditor),
  listerRoute(client, debtor),
  listerRoute(client, estate),
  headleaseRoute(client),
  listerRoute(client, lease),
  listerRoute(client, landlord),
  listerRoute(client, manager),
  listerRoute(client, property),
  listerRoute(client, supplier),
  listerRoute(client, tenant),
  listerRoute(client, tenantParent),
  unitRoute(client),
  vacantRoute(client),

  // Property
  allRoute(property),
  listerRoute(property, client),
  listerRoute(property, clientParent),
  listerRoute(property, creditor),
  listerRoute(property, debtor),
  listerRoute(property, estate),
  headleaseRoute(property),
  listerRoute(property, lease),
  listerRoute(property, landlord),
  listerRoute(property, manager),
  listerRoute(property, supplier),
  listerRoute(property, tenant),
  listerRoute(property, tenantParent),
  unitRoute(property),
  vacantRoute(property),

  // Manager
  allRoute(manager),
  listerRoute(manager, client),
  listerRoute(manager, clientParent),
  listerRoute(manager, creditor),
  listerRoute(manager, debtor),
  listerRoute(manager, estate),
  headleaseRoute(manager),
  listerRoute(manager, lease),
  listerRoute(manager, landlord),
  listerRoute(manager, property),
  listerRoute(manager, supplier),
  listerRoute(manager, tenant),
  unitRoute(manager),
  listerRoute(manager, vacant),
  listerRoute(manager, tenantParent),
  vacantRoute(manager),

  // Landlord
  allRoute(landlord),
  listerRoute(landlord, client),
  listerRoute(landlord, clientParent),
  listerRoute(landlord, creditor),
  listerRoute(landlord, debtor),
  listerRoute(landlord, estate),
  headleaseRoute(landlord),
  listerRoute(landlord, lease),
  listerRoute(landlord, manager),
  listerRoute(landlord, property),
  listerRoute(landlord, supplier),
  listerRoute(landlord, tenant),
  listerRoute(landlord, tenantParent),
  unitRoute(landlord),
  vacantRoute(landlord),

  // Client parent
  allRoute(clientParent),
  listerRoute(clientParent, client),
  listerRoute(clientParent, creditor),
  listerRoute(clientParent, debtor),
  listerRoute(clientParent, estate),
  headleaseRoute(clientParent),
  listerRoute(clientParent, lease),
  listerRoute(clientParent, landlord),
  listerRoute(clientParent, manager),
  listerRoute(clientParent, property),
  listerRoute(clientParent, supplier),
  listerRoute(clientParent, tenant),
  listerRoute(clientParent, tenantParent),
  unitRoute(clientParent),
  vacantRoute(clientParent),

  // Creditor
  allRoute(creditor),
  listerRoute(creditor, client),
  listerRoute(creditor, clientParent),
  listerRoute(creditor, debtor),
  listerRoute(creditor, estate),
  headleaseRoute(creditor),
  listerRoute(creditor, lease),
  listerRoute(creditor, landlord),
  listerRoute(creditor, manager),
  listerRoute(creditor, property),
  listerRoute(creditor, supplier),
  listerRoute(creditor, tenant),
  listerRoute(creditor, tenantParent),
  unitRoute(creditor),
  vacantRoute(creditor),

  // Tenant
  allRoute(tenant),
  listerRoute(tenant, client),
  listerRoute(tenant, clientParent),
  listerRoute(tenant, creditor),
  listerRoute(tenant, debtor),
  listerRoute(tenant, estate),
  headleaseRoute(tenant),
  listerRoute(tenant, lease),
  listerRoute(tenant, landlord),
  listerRoute(tenant, manager),
  listerRoute(tenant, property),
  listerRoute(tenant, supplier),
  listerRoute(tenant, tenantParent),
  unitRoute(tenant),
  vacantRoute(tenant),

  // Tenant Parent
  allRoute(tenantParent),
  listerRoute(tenantParent, client),
  listerRoute(tenantParent, clientParent),
  listerRoute(tenantParent, creditor),
  listerRoute(tenantParent, debtor),
  listerRoute(tenantParent, estate),
  headleaseRoute(tenantParent),
  listerRoute(tenantParent, lease),
  listerRoute(tenantParent, landlord),
  listerRoute(tenantParent, manager),
  listerRoute(tenantParent, property),
  listerRoute(tenantParent, tenant),
  unitRoute(tenantParent),

  // Supplier
  allRoute(supplier),
  listerRoute(supplier, client),
  listerRoute(supplier, clientParent),
  listerRoute(supplier, creditor),
  listerRoute(supplier, debtor),
  listerRoute(supplier, estate),
  headleaseRoute(supplier),
  listerRoute(supplier, lease),
  listerRoute(supplier, landlord),
  listerRoute(supplier, manager),
  listerRoute(supplier, property),
  listerRoute(supplier, tenant),
  listerRoute(supplier, tenantParent),
  unitRoute(supplier),

  // Debtor
  allRoute(debtor),
  listerRoute(debtor, client),
  listerRoute(debtor, clientParent),
  listerRoute(debtor, creditor),
  listerRoute(debtor, supplier),
  listerRoute(debtor, estate),
  headleaseRoute(debtor),
  listerRoute(debtor, lease),
  listerRoute(debtor, landlord),
  listerRoute(debtor, manager),
  listerRoute(debtor, property),
  listerRoute(debtor, tenant),
  listerRoute(debtor, tenantParent),
  unitRoute(debtor),
  vacantRoute(debtor),

  // Unit
  allRoute(unit),
  listerRoute(unit, client),
  listerRoute(unit, clientParent),
  listerRoute(unit, creditor),
  listerRoute(unit, supplier),
  listerRoute(unit, estate),
  headleaseRoute(unit),
  listerRoute(unit, lease),
  listerRoute(unit, landlord),
  listerRoute(unit, manager),
  listerRoute(unit, property),
  listerRoute(unit, tenant),
  listerRoute(unit, debtor),
  listerRoute(unit, vacant),
  listerRoute(unit, tenantParent),
  vacantRoute(unit),

  // Vacant
  allRoute(vacant),
  listerRoute(vacant, client),
  listerRoute(vacant, clientParent),
  listerRoute(vacant, creditor),
  listerRoute(vacant, supplier),
  listerRoute(vacant, estate),
  headleaseRoute(vacant),
  listerRoute(vacant, lease),
  listerRoute(vacant, landlord),
  listerRoute(vacant, manager),
  listerRoute(vacant, property),
  listerRoute(vacant, tenant),
  listerRoute(vacant, debtor),
  listerRoute(vacant, tenantParent),
  unitRoute(vacant),

  // Headlease
  allRoute(headlease),
  listerRoute(headlease, client),
  listerRoute(headlease, clientParent),
  listerRoute(headlease, creditor),
  listerRoute(headlease, supplier),
  listerRoute(headlease, estate),
  listerRoute(headlease, vacant),
  listerRoute(headlease, lease),
  listerRoute(headlease, landlord),
  listerRoute(headlease, manager),
  listerRoute(headlease, property),
  listerRoute(headlease, tenant),
  listerRoute(headlease, debtor),
  listerRoute(headlease, tenantParent),
  unitRoute(headlease),
  vacantRoute(headlease),

  // Lease
  allRoute(lease),
  listerRoute(lease, client),
  listerRoute(lease, clientParent),
  listerRoute(lease, creditor),
  listerRoute(lease, supplier),
  listerRoute(lease, estate),
  listerRoute(lease, vacant),
  headleaseRoute(lease),
  listerRoute(lease, landlord),
  listerRoute(lease, manager),
  listerRoute(lease, property),
  listerRoute(lease, tenant),
  listerRoute(lease, debtor),
  listerRoute(lease, tenantParent),
  unitRoute(lease),
  vacantRoute(lease)
];
